import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, IconButton } from '@mui/material'
import { Close } from '@mui/icons-material'

import { getAnswerKey } from '../../../service/experimentService'
import usePyodideWorker, { cleanPythonError } from '../../reasoningGap/usePyodideWorker'
import CodeEditor from '../../workspace/components/CodeEditor'
import ProvidedCode from '../../workspace/components/ProvidedCode'
import TaskDescription from '../../workspace/components/TaskDescription'
import Terminal from '../../workspace/components/Terminal'
import TestCases from '../../workspace/components/TestCases'
import { RegionContext } from '../../workspace/context/RegionContext'
import { TelemetryContext } from '../../workspace/context/TelemetryContext'
import { usePanelResize } from '../../workspace/hooks/usePanelResize'
import { formatTestsForRender } from '../../workspace/utils/TestCases/testDataFormatter'
import { providedCodeOf } from '../testResults'
import { filteredText } from './experimentOverviewStats'
import { EDITED_SUFFIX, loadableItems } from './experimentPresenter'
import {
  exerciseSession,
  newRunMarker,
  presenterHasTests,
  presenterRunSource,
  presenterTestResults,
  readRunResults,
  testsCompletedText,
} from './experimentPresenterRun'

import '../../workspace/css/workspace.scss'

// The most of a run's output, and of its error, the terminal shows: a program printing in a loop
// must not hold up the page, and the word filter checks every word against its whole list
// (some 900 patterns) - about a fifth of a second for these 2,000 characters, on this page's
// own thread.
export const OUTPUT_MAX_CHARS = 2000

/**
 * What the terminal shows of a run's output - or, `fromEnd`, of its error: at most
 * OUTPUT_MAX_CHARS of it, the output's start or the error's end (where Python puts the error
 * itself), with '…' where it is cut, through the word filter - only what is shown goes through
 * it. { text, length, cut }: `length` is the whole text's.
 */
export const shownOutput = (value, { fromEnd = false } = {}) => {
  const text = typeof value === 'string' ? value : ''
  if (text.length <= OUTPUT_MAX_CHARS) return { text: filteredText(text), length: text.length, cut: false }
  const part = filteredText(fromEnd ? text.slice(-OUTPUT_MAX_CHARS) : text.slice(0, OUTPUT_MAX_CHARS))
  return { text: fromEnd ? `…${part}` : `${part}…`, length: text.length, cut: true }
}

const count = (n) => n.toLocaleString('en-US')
// What the terminal says of an output or error it cut (shownOutput's).
const cutNote = (what, shown, fromEnd = false) =>
  `${what} cut to its ${fromEnd ? 'last' : 'first'} ${count(OUTPUT_MAX_CHARS)} characters, of ${count(shown.length)}.`

/**
 * What a run (usePyodideWorker's run) came to: { output, error } - what it printed, and its
 * error: Python's, from the code's own lines on (cleanPythonError), or the runner's own message
 * for a run that timed out or a runtime that never loaded.
 */
export const runResult = (answer) => {
  const output = typeof answer?.stdout === 'string' ? answer.stdout : ''
  if (answer?.ok) return { output, error: '' }
  const error = answer?.timedOut || answer?.loadError ? String(answer.error || '') : cleanPythonError(answer?.error)
  return { output, error: error || 'The code could not be run.' }
}

/**
 * The student page's terminal lines for a run, as its terminal shows one (TerminalContext): a
 * line for each line the code printed, then its error, then 'Tests Completed: X%' when it has
 * test results - what the code printed and its error cut and through the word filter
 * (shownOutput), with a note for what was cut. `shown`: the run's { output, error, results }, or
 * { notice } for a Run that ran nothing (an exercise that is not Python).
 */
export const terminalOutput = (shown) => {
  if (!shown) return []
  if (shown.notice) return [{ type: 'stdout', content: shown.notice }]
  const output = shownOutput(shown.output)
  const error = shownOutput(shown.error, { fromEnd: true })
  const lines = []
  if (output.text) {
    output.text
      .replace(/\n$/, '')
      .split('\n')
      .forEach((content) => lines.push({ type: 'stdout', content }))
  }
  if (output.cut) lines.push({ type: 'stdout', content: cutNote('Output', output) })
  if (error.text) lines.push({ type: 'stderr', content: error.text })
  if (error.cut) lines.push({ type: 'stdout', content: cutNote('Error', error, true) })
  if (shown.results?.length) lines.push({ type: 'test_result', content: testsCompletedText(shown.results) })
  // Tests off and nothing printed: said, so the terminal does not look as if nothing ran.
  return lines.length ? lines : [{ type: 'stdout', content: 'It ran with no output.' }]
}

// The student page's panels log what is done in them and track where they are on the screen;
// here they report to these, which keep nothing: the instructor's page logs nothing and tracks
// nothing. (Made once, so the panels' effects do not run again on every render.)
const NO_TELEMETRY = { logTelemetryEvent: () => {} }
const NO_REGIONS = { updateRegion: () => {}, removeRegion: () => {} }
// The student page's panel sizes (usePanelResize), but a narrower right column - the list's rows
// are short, where the student page has the AI chat - and a terminal of at most 30% of the
// window's height: a projector is often small, or the page zoomed for the class.
const panelSizes = () => ({
  rightWidth: 320,
  terminalHeight: Math.round(Math.min(250, Math.max(120, (window.innerHeight || 0) * 0.3))),
})
// The least width the editor keeps when the window is narrow (fitRightPanel, below).
const MIN_EDITOR_PX = 560

const mono = 'Monaco, Menlo, monospace'
// Above the editor, as the student page's editor shows its files (CodeEditor's tab bar): what the
// editor holds, as a file tab, and what can be done with it.
const fileBar = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '6px 8px 0 8px',
  background: '#fafafa',
  borderBottom: '1px solid #e5e5e5',
  flexShrink: 0,
}
const fileTab = {
  minWidth: 0,
  padding: '6px 14px',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#171717',
  background: '#ffffff',
  border: '1px solid #e5e5e5',
  borderBottom: '1px solid #ffffff',
  borderRadius: '6px 6px 0 0',
  marginBottom: '-1px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
// As CodeEditor's Reset Template button.
const barButton = {
  padding: '4px 8px',
  fontSize: '0.725rem',
  fontWeight: 600,
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  backgroundColor: '#ffffff',
  color: '#374151',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}
const noteText = { margin: '0 0 12px 0', fontSize: '0.8125rem', lineHeight: 1.5, color: '#737373' }
// A row of the list: a button laid out as a list row, as the Overview's lists are.
const itemSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  padding: '9px 4px',
  border: 'none',
  background: 'none',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover': { background: '#f9fafb' },
}
const oneLine = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

// Waits between attempts at the answer key while the server does not answer: a few, then the
// Presenter goes on without it.
const ANSWER_KEY_RETRY_MS = [1000, 3000, 8000]

// Fetches the experiment's answer key once, when the Presenter mounts (the page mounts it anew
// for another experiment), and pins it above the list (useExperimentPresenter's setAnswerKey).
// None, or not for this account: no pinned row. No answer from the server, or a 5xx: tried
// again a few times, quietly - the Presenter works the same without it.
const useAnswerKey = (experimentId, setAnswerKey) => {
  useEffect(() => {
    if (experimentId == null || !setAnswerKey) return undefined
    let cancelled = false
    let timer = null
    const attempt = async (tries) => {
      try {
        const key = await getAnswerKey(experimentId)
        if (!cancelled) setAnswerKey(key)
      } catch (error) {
        const status = error?.response?.status
        if (cancelled || (status && status < 500) || tries >= ANSWER_KEY_RETRY_MS.length) return
        timer = setTimeout(() => attempt(tries + 1), ANSWER_KEY_RETRY_MS[tries])
      }
    }
    attempt(0)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [experimentId, setAnswerKey])
}

// A row of the list: the code it loads, marked while it is the one in the editor. `pinned`: the
// answer key's row, which has a plain note and cannot be removed.
const ListRow = ({ item, isSelected, onSelect, onRemove, pinned = false }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      borderBottom: pinned ? '1px solid #e5e7eb' : '1px solid #f3f4f6',
      background: isSelected ? '#eff6ff' : 'none',
    }}>
    <Box
      component="button"
      type="button"
      sx={itemSx}
      aria-pressed={isSelected}
      title={item.detail ? `${item.label}\n${item.detail}` : item.label}
      onClick={onSelect}>
      <span style={{ ...oneLine, fontSize: '14px', color: '#111', fontWeight: isSelected ? 600 : 400 }}>{item.label}</span>
      {item.detail && (
        <span style={{ ...oneLine, fontSize: '12px', color: '#6b7280', fontFamily: pinned ? 'inherit' : mono }}>
          {item.detail}
        </span>
      )}
    </Box>
    {!pinned && (
      <IconButton size="small" aria-label={`Remove ${item.label}`} onClick={onRemove}>
        <Close sx={{ fontSize: 16, color: '#9ca3af' }} />
      </IconButton>
    )}
  </div>
)

// The right column, where the student page has the AI chat: the code added from the Overview,
// under the experiment's answer key when it has one (pinned: not removed, nor cleared).
const AddedCode = ({ presenter }) => (
  <div className="ws-panel-content">
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '0.75rem' }}>
      <h3 className="ws-section-title" style={{ margin: 0 }}>
        Added code
      </h3>
      {presenter.items.length > 0 && (
        <button type="button" style={barButton} onClick={presenter.clear}>
          Clear list
        </button>
      )}
    </div>
    <p style={noteText}>Students&apos; code added from the Overview.</p>
    {presenter.answerKey && (
      <ListRow
        item={presenter.answerKey}
        pinned
        isSelected={presenter.answerKey.id === presenter.selectedId}
        onSelect={() => presenter.select(presenter.answerKey.id)}
      />
    )}
    {presenter.items.length === 0 ? (
      <p style={{ ...noteText, marginTop: presenter.answerKey ? '12px' : 0 }}>
        Nothing added yet. On the Overview, open a student&apos;s code - from Common errors, Tests students are stuck on,
        or a student&apos;s detail - and press Show in Presenter.
      </p>
    ) : (
      presenter.items.map((item) => (
        <ListRow
          key={item.id}
          item={item}
          isSelected={item.id === presenter.selectedId}
          onSelect={() => presenter.select(item.id)}
          onRemove={() => presenter.remove(item.id)}
        />
      ))
    )}
  </div>
)

// The Presenter's page, inside ExperimentPresenterTab's providers: usePanelResize logs a resize
// through the context as well.
const PresenterWorkspace = ({ presenter, exercise: exerciseRow, runnable, exerciseType }) => {
  const { status, run } = usePyodideWorker()
  useAnswerKey(presenter.experimentId, presenter.setAnswerKey)
  const containerRef = useRef(null)
  const [initialSizes] = useState(panelSizes)
  const layout = usePanelResize(containerRef, initialSizes)
  // The side panels give way to the editor (usePanelResize's fitRightPanel: the right one, then
  // the left, never below 200px) when the Presenter is first shown, when the window is resized
  // or zoomed, and when its tab is shown again (hidden, it has no width and nothing changes).
  const { fitRightPanel } = layout
  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined
    const fit = () => fitRightPanel(MIN_EDITOR_PX)
    fit()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fit)
      return () => window.removeEventListener('resize', fit)
    }
    const observer = new ResizeObserver(fit)
    observer.observe(container)
    return () => observer.disconnect()
  }, [fitRightPanel])
  const exercise = useMemo(() => exerciseSession(exerciseRow), [exerciseRow])
  const hasTests = presenterHasTests(exercise)
  const [running, setRunning] = useState(false)
  // The last run's { output, error, results } (or { notice }), until the next, with the code it
  // ran and the editor's load it ran in (useExperimentPresenter's `loads`).
  const [result, setResult] = useState(null)
  // The item loaded into the editor: a listed one, or the pinned answer key.
  const selected = loadableItems(presenter).find((item) => item.id === presenter.selectedId) || null
  const edited = Boolean(selected) && presenter.code !== selected.code
  const label = selected
    ? `${selected.label}${edited && !selected.label.endsWith(EDITED_SUFFIX) ? EDITED_SUFFIX : ''}`
    : 'Your own code'

  const runTests = async () => {
    if (running) return
    const ran = { code: presenter.code, loads: presenter.loads }
    if (!runnable) {
      setResult({
        ...ran,
        notice: `Run is for Python exercises${exerciseType ? `, and this experiment's exercise is ${exerciseType}` : ''}. The code is shown as it is.`,
      })
      return
    }
    setRunning(true)
    try {
      const marker = newRunMarker()
      const answer = await run(presenterRunSource(exercise, ran.code, marker))
      const { output, results } = readRunResults(answer, marker)
      setResult({ ...ran, ...runResult({ ...answer, stdout: output }), results: presenterTestResults(results, exercise) })
    } catch (error) {
      setResult({ ...ran, output: '', error: 'The code could not be run. Try again.', results: presenterTestResults([], exercise) })
    } finally {
      setRunning(false)
    }
  }

  // The last run's, while no other code was loaded into the editor since, or while it holds the
  // code that ran again: never another program's output or results with the code shown.
  const shown = result && (result.loads === presenter.loads || result.code === presenter.code) ? result : null
  // As on the student page, the terminal empties when a run starts; the tests keep their results
  // until the run's own come.
  const output = useMemo(() => (running ? [] : terminalOutput(shown)), [running, shown])
  const tests = formatTestsForRender(exercise, shown?.results || [])

  return (
    <div className="ws-container">
      <div className="ws-content" ref={containerRef}>
        <div className="ws-panel-left" style={{ width: `${layout.leftWidth}px` }}>
          <div className="ws-panel-content">
            {exercise ? (
              <>
                <TaskDescription task={exercise.task} />
                {/* The exercise's provided code (E3's Person), as students see it; Run loads it too. */}
                {providedCodeOf(exercise.test_code) && <ProvidedCode code={providedCodeOf(exercise.test_code).code} />}
                {/* Hidden when the instructor turned test cases off (enable_tests), as for students. */}
                {hasTests && (
                  <>
                    <div className="ws-section-divider"></div>
                    <TestCases tests={tests} />
                  </>
                )}
              </>
            ) : (
              <TaskDescription task="Loading the exercise…" />
            )}
          </div>
        </div>
        <div className="ws-resizer ws-horizontal" onMouseDown={layout.startDraggingLeft} />

        {/* The editor ends where the terminal starts (as on the student page), so the end of a
            long program can be scrolled into view. */}
        <div className="ws-middle-panel" style={{ paddingBottom: `${layout.terminalHeight}px` }}>
          <div style={fileBar} role="toolbar" aria-label="Editor">
            <span style={fileTab} title={label}>
              {label}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '5px', flexShrink: 0 }}>
              {status === 'loading' && runnable && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#9ca3af' }}>
                  <CircularProgress size={12} /> Loading Python…
                </span>
              )}
              {edited && (
                <button type="button" style={barButton} onClick={presenter.reset} title="Put the added code back as it was">
                  Undo changes
                </button>
              )}
              <button
                type="button"
                style={barButton}
                onClick={presenter.blank}
                title="Empty the editor, to write your own code. What you typed there goes to the list.">
                Blank editor
              </button>
            </div>
          </div>
          {/* The student page's editor, which takes its code when it mounts: code loaded into it
              (Show in Presenter, the list, Blank editor, Undo changes) mounts it anew with that code. */}
          <CodeEditor key={presenter.loads} initialCode={presenter.code} onCodeChange={presenter.setCode} language="python" />
          <Terminal height={layout.terminalHeight} onRun={runTests} isRunning={running} output={output} hasTests={hasTests} />
          <div
            className="ws-resizer ws-vertical"
            style={{ position: 'absolute', bottom: layout.terminalHeight, left: 0, right: 0, zIndex: 30 }}
            onMouseDown={layout.startDraggingTerminal}
          />
        </div>

        <div className="ws-resizer ws-horizontal" onMouseDown={layout.startDraggingRight} />
        <div className="ws-panel-right" style={{ width: `${layout.rightWidth}px` }}>
          <AddedCode presenter={presenter} />
        </div>
      </div>
    </div>
  )
}

/**
 * The experiment page's Presenter: code the instructor shows the class and runs - their own, or a
 * student's added from the Overview ("Show in Presenter") - laid out as the student page
 * (Workspace): the exercise's task and tests on the left, the editor and the terminal in the
 * middle, and on the right, where students have the AI chat, the list of what was added, any of
 * which loads into the editor - under the experiment's answer key, pinned at its top when the
 * server has one for this instructor (fetched here, once; never stored with the list, removed or
 * cleared). Its panels are the student page's own. It is projected, so a
 * student appears by their Overview label only ('Student 7 · NameError'), and what they wrote
 * comes through the word filter (experimentPresenter.addToPresenter) - what their code printed
 * too. A run's output and test results show with the code that produced them: loading other code
 * into the editor (Show in Presenter, the list, Blank editor, Undo changes) takes them away,
 * unless that is the same code; typing in the editor leaves them until the next run. What the
 * instructor typed goes to the list before other code replaces it
 * (experimentPresenter.keepEditorCode).
 *
 * Run Tests runs what a student's Run Tests runs (experimentPresenterRun), here, in this browser,
 * in a Web Worker (the reasoning-gap page's runner): a student's code cannot reach this page or the
 * instructor's sign-in, and an endless loop stops after 20 s instead of freezing the page. Hidden
 * tests show their name and whether they passed, as students see them. Nothing is saved or
 * logged, and students see nothing of it on their own pages.
 *   presenter: useExperimentPresenter's (the page's state, so the Overview can add to it);
 *   exercise: the experiment's exercise, a condition's session row (null until loaded);
 *   runnable: whether the exercise is Python (Run runs nothing otherwise);
 *   exerciseType: its session type, to say so.
 */
const ExperimentPresenterTab = ({ presenter, exercise = null, runnable = true, exerciseType = null }) => (
  <TelemetryContext.Provider value={NO_TELEMETRY}>
    <RegionContext.Provider value={NO_REGIONS}>
      <PresenterWorkspace presenter={presenter} exercise={exercise} runnable={runnable} exerciseType={exerciseType} />
    </RegionContext.Provider>
  </TelemetryContext.Provider>
)

export default ExperimentPresenterTab
